import {
	BENCH_FRAME_HEIGHT_MM,
	BENCH_SLAT_GAP_MM,
	BENCH_SLAT_THICKNESS_MM,
	BENCH_SLAT_WIDTH_MM,
} from "./constants";
import { resolveBenchSupport } from "./benchSupport";
import {
	type BenchRun,
	type InteriorRoom,
	wallAxisLengthMm,
	wallRunsAlongX,
} from "./types";

/**
 * Air gap left open between the floor and the lowest skirt board. Cold floor
 * air has to reach the space under the lavar, and a mop has to reach the floor.
 */
export const BENCH_SKIRT_FLOOR_GAP_MM = 100;

/**
 * The slatted front (spjälfront) that closes the foot lav down toward the floor.
 *
 * Without it a nedre lav stands on bare 45×70 posts, and from the room those
 * posts read as a row of long sticks under a floating shelf. The Swedish build
 * guides dress the frame instead: Vi i Villa's lav closes each frame with a
 * front board, and a finished bastu closes the foot lav's face with the same
 * 28×90 kvistfritt as the seat, laid with the seat's 18 mm air gaps so the
 * space under the lavar still breathes.
 *
 * Boards run horizontally under the front rail. The front covers the run's full
 * seat length; a run end that stops short of a wall gets a matching end skirt
 * so the posts are not simply visible from the side instead.
 */
export interface BenchSkirt {
	/** Horizontal boards stacked on each skirted face. */
	boardCount: number;
	/** Front boards span the seat, flush with its ends. */
	frontLengthMm: number;
	/**
	 * End boards run from behind the front skirt back to the wall. A foot lav
	 * standing in front of the upper lav reaches past its own depth, closing
	 * the space under the upper lav too, or its posts show end-on instead.
	 */
	endLengthMm: number;
	/** Top of the highest board, above the finished floor. */
	topMm: number;
	/** Bottom of the lowest board, above the finished floor. */
	bottomMm: number;
	/** Run start (the low coordinate along the wall) is open to the room. */
	openAtStart: boolean;
	/** Run end (the high coordinate along the wall) is open to the room. */
	openAtEnd: boolean;
	/** Running metres of 28×90 the skirt buys. */
	lpm: number;
}

/**
 * Any gap between a run's end and the wall leaves the posts on show: a 50 or
 * 100 mm corner gap exposes them at an angle, and through a panorama wall the
 * whole end is in view. Only an end that meets the wall is closed.
 */
const OPEN_END_MIN_GAP_MM = 1;

/**
 * The skirt a run gets, or null. Only a sauna foot lav standing on its own
 * posts is skirted: an upper lav's posts stand behind the foot lav, a wall-hung
 * or end-supported run has no posts to hide, and the relax bench is furniture.
 */
export function resolveBenchSkirt(
	run: BenchRun,
	room?: InteriorRoom,
): BenchSkirt | null {
	if (run.skirtEnabled === false) return null;
	if (run.skirtEnabled !== true && (run.room !== "sauna" || run.tier !== "lower")) return null;
	const { support, legHeightMm } = resolveBenchSupport(run, room);
	if (support === "endWalls" || support === "wall" || legHeightMm <= 0) {
		return null;
	}
	const pitchMm = BENCH_SLAT_WIDTH_MM + BENCH_SLAT_GAP_MM;
	// The front rail closes the top of the face; boards hang below it with the
	// same shadow gap the seat slats use.
	const topMm = legHeightMm - BENCH_FRAME_HEIGHT_MM - BENCH_SLAT_GAP_MM;
	const boardCount = Math.floor(
		(topMm - BENCH_SKIRT_FLOOR_GAP_MM + BENCH_SLAT_GAP_MM) / pitchMm,
	);
	if (boardCount < 1) return null;
	const bottomMm = topMm - boardCount * pitchMm + BENCH_SLAT_GAP_MM;

	const axisLengthMm = room ? wallAxisLengthMm(room, run.wall) : null;
	// A run starts at the wall's low-coordinate corner: −X (left) for a run
	// along X, −Z (back) for one along Z. An end against panorama glass is
	// as exposed as a free one: through the glass it is the first thing seen.
	const alongX = wallRunsAlongX(run.wall);
	const glazed = room?.glazedWalls ?? [];
	const openAtStart =
		run.startMm >= OPEN_END_MIN_GAP_MM ||
		glazed.includes(alongX ? "left" : "back");
	const openAtEnd =
		(axisLengthMm !== null &&
			axisLengthMm - (run.startMm + run.lengthMm) >= OPEN_END_MIN_GAP_MM) ||
		glazed.includes(alongX ? "right" : "front");

	// Posted runs are never end-supported, so the seat is the full run length.
	const frontLengthMm = run.lengthMm;
	const endLengthMm =
		run.offsetFromWallMm + run.depthMm - BENCH_SLAT_THICKNESS_MM;
	const endCount = Number(openAtStart) + Number(openAtEnd);
	return {
		boardCount,
		frontLengthMm,
		endLengthMm,
		topMm,
		bottomMm,
		openAtStart,
		openAtEnd,
		lpm: (boardCount * (frontLengthMm + endCount * endLengthMm)) / 1000,
	};
}
